import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import logo from '../assets/safety.png';
import BrandWordmark from '../components/BrandWordmark';

/**
 * Asset QR Code Redirect Page
 * 
 * This page opens when users scan QR codes with their phone's camera.
 * It automatically attempts to open the Check Now mobile app with the scanned asset.
 * 
 * Flow:
 * 1. User scans QR code: https://app.safetychecknow.com/asset/SERIAL123
 * 2. This page loads in browser
 * 3. Automatically redirects to: checknow://asset/SERIAL123
 * 4. Mobile app opens with the asset
 */

const AssetRedirect: React.FC = () => {
  const { serial } = useParams<{ serial: string }>();
  const [hasTriedOpening, setHasTriedOpening] = useState(false);

  useEffect(() => {
    if (!serial) return;

    const deepLinkUrl = `checknow://asset/${encodeURIComponent(serial)}`;
    console.log('📱 Attempting to open app with:', deepLinkUrl);

    // Detect platform
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /android/i.test(navigator.userAgent);

    // Try to open the app
    setTimeout(() => {
      try {
        if (isIOS) {
          // For iOS, create a link and click it
          const link = document.createElement('a');
          link.href = deepLinkUrl;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else if (isAndroid) {
          // For Android, use intent URL
          const intentUrl = `intent://asset/${encodeURIComponent(serial)}#Intent;scheme=checknow;package=com.jacobhartwell.SafetyCheckNow;end`;
          window.location.href = intentUrl;
        } else {
          // Desktop - just try the deep link
          window.location.href = deepLinkUrl;
        }

        setHasTriedOpening(true);

        // After 2 seconds, show download option
        setTimeout(() => {
          // If still on this page, user might not have the app
          const appStoreUrl = isIOS 
            ? 'https://apps.apple.com/us/app/check-now/id6745303459'
            : 'https://play.google.com/store/apps/details?id=com.jacobhartwell.SafetyCheckNow';
          
          const shouldRedirect = window.confirm(
            'Check Now app not installed?\n\nClick OK to download from the App Store or Google Play.'
          );
          
          if (shouldRedirect) {
            window.location.href = appStoreUrl;
          }
        }, 2000);

      } catch (error) {
        console.error('Error opening app:', error);
        setHasTriedOpening(true);
      }
    }, 800);
  }, [serial]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)',
        color: 'white',
        padding: 3,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          maxWidth: 400,
          animation: 'fadeIn 0.6s ease-out',
          '@keyframes fadeIn': {
            from: { opacity: 0, transform: 'translateY(20px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        {/* Logo */}
        <Box
          component="img"
          src={logo}
          alt="Check Now"
          sx={{
            width: 96,
            height: 96,
            borderRadius: '22px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            background: 'white',
            padding: 1,
            marginBottom: 3,
          }}
        />

        {/* Brand */}
        <Box mb={1}>
          <BrandWordmark variant="h3" />
        </Box>
        <Typography variant="body1" sx={{ opacity: 0.85, mb: 4 }}>
          Safety & Asset Management
        </Typography>

        {/* Status */}
        <Box
          sx={{
            background: 'rgba(255, 255, 255, 0.15)',
            borderRadius: 4,
            padding: 3,
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Typography variant="h2" mb={2}>
            {hasTriedOpening ? '📱' : '🔓'}
          </Typography>
          <Typography variant="h6" fontWeight={600} mb={2}>
            {hasTriedOpening ? 'Opening App' : 'Loading Asset'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              background: 'rgba(0, 0, 0, 0.2)',
              padding: 1,
              borderRadius: 2,
              mb: 2,
            }}
          >
            {serial}
          </Typography>
          <CircularProgress
            size={36}
            sx={{
              color: 'white',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
        </Box>

        {/* Help Text */}
        <Typography variant="body2" sx={{ mt: 3, opacity: 0.7 }}>
          {hasTriedOpening
            ? 'If the app doesn\'t open automatically, you may need to install it.'
            : 'Redirecting to Check Now mobile app...'}
        </Typography>
      </Box>
    </Box>
  );
};

export default AssetRedirect;


